/* eslint-disable react/jsx-max-props-per-line */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import BaseService from '../../../../../services/BaseService';
import { forcarEnvioIntegracaoNsmp } from '../utils/forcarEnvioIntegracao';
import { renderStatus } from '../../../../../components/v2/cell-renderers/status';
import { useDigitacaoEventos } from '../hooks/useDigitacaoEventos';
import {
  formatDigitadoEm,
  getIntegracaoDetail,
  getStatusAprovacaoLabel,
  integracaoChipFromRow,
  ORIGEM_LABELS,
} from '../utils/integracaoStatus';
import { formatAlteracaoLine } from '../utils/formatAlteracao';

const DigitacaoTab = ({
  nsmpId,
  nsmpRow,
  readOnly,
  participacaoMode,
  podeForcarEnvio,
  userId,
  onReprocessDone,
  onEnviado,
}) => {
  const {
    eventos,
    integracao,
    loading,
    loadError,
    refetch,
  } = useDigitacaoEventos(nsmpId, {
    enabled: !!nsmpId,
    participacaoMode,
    userId,
    fallbackIntegracao: nsmpRow?.resumoIntegracao,
  });

  const [reprocessing, setReprocessing] = React.useState(false);
  const [sendError, setSendError] = React.useState(null);

  const effectiveRow = useMemo(() => ({
    ...nsmpRow,
    totalEventos: nsmpRow?.totalEventos ?? eventos.length,
    resumoIntegracao: {
      ...(nsmpRow?.resumoIntegracao || {}),
      ...(integracao || {}),
      status: integracao?.status ?? nsmpRow?.resumoIntegracao?.status,
      erro: integracao?.erro ?? nsmpRow?.resumoIntegracao?.erro,
    },
  }), [nsmpRow, integracao, eventos.length]);

  const chip = integracaoChipFromRow(effectiveRow);
  const detail = getIntegracaoDetail(effectiveRow);
  const integracaoStatus = effectiveRow?.resumoIntegracao?.status;
  const statusAprovacaoLabel = getStatusAprovacaoLabel(detail?.statusAprovacao);

  const handleReprocess = async () => {
    const entityId =
      integracao?.integrateEntityId
      || nsmpRow?.resumoIntegracao?.integrateEntityId;
    if (!entityId || readOnly || participacaoMode === 'CONSULTA') return;
    setReprocessing(true);
    setSendError(null);
    try {
      await BaseService.post({ url: `integrate-entities/${entityId}/reprocessar` });
      await refetch();
      onReprocessDone?.();
    } catch (error) {
      setSendError(
        error?.response?.data?.mensagem
        || error?.response?.data?.message
        || 'Não foi possível reprocessar a integração.',
      );
    } finally {
      setReprocessing(false);
    }
  };

  const handleForceSend = async () => {
    if (!nsmpId || !podeForcarEnvio) return;
    setReprocessing(true);
    setSendError(null);
    try {
      const result = await forcarEnvioIntegracaoNsmp(nsmpId);
      if (!result.ok) {
        setSendError('Não foi possível enviar a integração.');
        return;
      }
      onEnviado?.(nsmpId);
    } catch (error) {
      setSendError(
        error?.response?.data?.mensagem
        || error?.response?.data?.message
        || 'Não foi possível enviar a integração.',
      );
    } finally {
      setReprocessing(false);
    }
  };

  const canRetry =
    !readOnly
    && participacaoMode === 'ATIVO'
    && integracaoStatus === 'ERROR_SYNC'
    && (integracao?.integrateEntityId || nsmpRow?.resumoIntegracao?.integrateEntityId);

  const canForceSend =
    podeForcarEnvio
    && integracaoStatus === 'NOT_SYNC'
    && !!nsmpId;

  const sendLabel = reprocessing
    ? 'Enviando…'
    : canForceSend
      ? 'Enviar agora'
      : 'Executar agora';

  const alertSeverity = detail?.severity === 'error'
    ? 'error'
    : detail?.severity === 'success'
      ? 'success'
      : 'info';

  return (
    <Box sx={{ pt: 1 }}>
      {chip && detail && (
        <Alert
          severity={alertSeverity}
          variant="outlined"
          sx={{ mb: 2 }}
          action={
            canRetry || canForceSend ? (
              <Button
                color="inherit"
                size="small"
                onClick={canForceSend ? handleForceSend : handleReprocess}
                disabled={reprocessing}
              >
                {sendLabel}
              </Button>
            ) : null
          }
        >
          <Stack spacing={0.5}>
            {renderStatus(chip)}
            {detail.origemLabel && (
              <Typography variant="caption" color="text.secondary">
                {detail.origemLabel}
                {statusAprovacaoLabel ? ` · ${statusAprovacaoLabel}` : ''}
              </Typography>
            )}
            <Typography variant="caption">{detail.body}</Typography>
            {sendError && (
              <Typography variant="caption" color="error.main">
                {sendError}
              </Typography>
            )}
            {integracao?.erro && integracaoStatus === 'ERROR_SYNC' && (
              <Typography variant="caption">{integracao.erro}</Typography>
            )}
            {integracaoStatus === 'ERROR_SYNC' && (
              <Typography variant="caption" color="text.secondary">
                Próxima tentativa automática em até 1h30.
              </Typography>
            )}
          </Stack>
        </Alert>
      )}

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Eventos de digitação
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : eventos.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {loadError
            ? 'Nenhum evento registrado nesta NSMP (ou endpoint ainda indisponível).'
            : 'Nenhuma digitação registrada nesta NSMP.'}
        </Typography>
      ) : (
        <Stack spacing={1}>
          {eventos.map((ev) => (
            <Box
              key={ev.id || ev.ordem}
              sx={{
                p: 1.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                #{ev.ordem}{' '}
                {formatDigitadoEm(ev.digitadoEm) || '—'}{' '}
                · {ev.digitadorNome}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {ev.origemLabel || ORIGEM_LABELS[ev.origem] || ev.origem || '—'}
                {(ev.statusAprovacaoLabel || ev.statusAprovacao)
                  ? ` · ${ev.statusAprovacaoLabel || getStatusAprovacaoLabel(ev.statusAprovacao)}`
                  : ''}
              </Typography>
              {Array.isArray(ev.alteracoes) && ev.alteracoes.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {ev.alteracoes.map((alt) => {
                    const line = formatAlteracaoLine(alt);
                    if (!line) return null;
                    return (
                      <Typography
                        key={alt.campo}
                        variant="caption"
                        display="block"
                        color="text.secondary"
                        sx={{ lineHeight: 1.6 }}
                      >
                        {line}
                      </Typography>
                    );
                  })}
                </Box>
              )}
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

DigitacaoTab.propTypes = {
  nsmpId: PropTypes.string,
  nsmpRow: PropTypes.object,
  readOnly: PropTypes.bool,
  participacaoMode: PropTypes.string,
  podeForcarEnvio: PropTypes.bool,
  userId: PropTypes.string,
  onReprocessDone: PropTypes.func,
  onEnviado: PropTypes.func,
};

DigitacaoTab.defaultProps = {
  nsmpId: null,
  nsmpRow: null,
  readOnly: false,
  participacaoMode: 'ATIVO',
  podeForcarEnvio: false,
  userId: null,
  onReprocessDone: undefined,
  onEnviado: undefined,
};

export default DigitacaoTab;
